<script setup lang="ts">
  import { BasePopup, PhoneInput, BaseButton, VLoader } from '@qic/ui-components'
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import WhatsAppButton from '@/entities/Flow/components/WhatsAppButton/WhatsAppButton.vue'
  import { useGoogleAnalytics, useValidation, useValidators } from '@/shared/composables'
  import { useCommonStore } from '@/shared/store'
  import {
    UAE_PHONE_CODE,
    BUTTON_SEND_CALLBACK_TEST_ID,
    BASE_POPUP_TEST_ID,
    PHONE_INPUT_TEST_ID,
  } from './ContactUsWidget.constants'
  import FlowPrivacyPolicyReminder from '@/entities/Flow/components/FlowPrivacyPolicyReminder/FlowPrivacyPolicyReminder.vue'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { CONTACTS_KEYS } from '@/shared/types/common.types'

  interface Props {
    modelValue?: boolean
    page?: AvailableFlowRoutes
    dataTest?: string
  }

  interface Emits {
    (e: 'update:modelValue', value: boolean): void
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    dataTest: BASE_POPUP_TEST_ID,
  })

  const emit = defineEmits<Emits>()

  const gaService = useGoogleAnalytics()
  const commonStore = useCommonStore()
  const phone = ref('')
  const phoneCompleted = ref(false)
  const callbackSent = ref(false)
  const isCallbackSendingProgress = ref(false)
  const { locale } = useI18n()
  const isRtl = computed(() => locale.value === 'ar')

  const countryCode = ref(UAE_PHONE_CODE)
  const { phoneValidator, required } = useValidators()
  const validationRules = {
    phone: {
      required,
    },
    phoneCompleted: {
      ...phoneValidator,
    },
  }
  const { v$, errors } = useValidation(validationRules, { phone, phoneCompleted })

  const sendCallback = async () => {
    v$.value.$touch()
    if (v$.value.$error || isCallbackSendingProgress.value) return
    try {
      isCallbackSendingProgress.value = true
      await commonStore.sendCallback(phone.value)
      if (props.page) {
        gaService.sendClickPhone(props.page, phone.value)
      }
      callbackSent.value = true
    } catch (err) {
      console.error(err)
    } finally {
      isCallbackSendingProgress.value = false
    }
  }

  const onClose = () => {
    callbackSent.value = false
    isCallbackSendingProgress.value = false
    emit('update:modelValue', false)
  }

  const sendWhatsAppEvent = () => (props.page ? gaService.sendWhatsApp(props.page) : null)

  const waLink = computed(() => commonStore.getContactValue(CONTACTS_KEYS.WA_LINK_GENERAL))

  watch(
    () => props.modelValue,
    () => {
      if (props.modelValue) {
        commonStore.getContacts()
      }
    }
  )
</script>

<template>
  <Teleport to="body">
    <BasePopup
      v-if="modelValue"
      :is-rtl="isRtl"
      big
      :use-button="false"
      :data-test="dataTest"
      @close="onClose"
    >
      <div class="need-help">
        <div v-if="!callbackSent" class="need-help__content">
          <h3 class="need-help__title">
            {{ $t('contact_us_popup.heading') }}
          </h3>
          <div class="need-help__row">
            <p class="need-help__text">
              {{ $t('contact_us_popup.whatsapp.title') }}
            </p>
            <WhatsAppButton
              :href="waLink"
              @on-click="sendWhatsAppEvent"
              data-test="contact-whatsapp-button"
            />
          </div>
          <div class="need-help__or-border"></div>
          <div class="need-help__row">
            <p class="need-help__text">
              {{ $t('contact_us_popup.phone.title') }}
            </p>
            <div class="need-help__phone">
              <PhoneInput
                v-model="phone"
                v-model:completed="phoneCompleted"
                :country-code="countryCode"
                :errors="errors.phoneCompleted"
                :data-test="PHONE_INPUT_TEST_ID"
              />
              <BaseButton
                medium
                icon="none"
                class="need-help__phone-button"
                :data-test="BUTTON_SEND_CALLBACK_TEST_ID"
                :disabled="isCallbackSendingProgress"
                @click="sendCallback"
              >
                <template v-if="isCallbackSendingProgress">
                  <VLoader color="#fff" />
                </template>
                <template v-else>
                  {{ $t('contact_us_popup.send.button') }}
                </template>
              </BaseButton>
            </div>
            <FlowPrivacyPolicyReminder
              place="popup_contact_us"
              class="need-help__reminder"
              data-test="popup-privacy-policy-reminder"
            />
          </div>
        </div>
        <div v-else class="need-help__content">
          <div class="need-help__row">
            <div class="need-help__recieved">
              {{ $t('qic_need_help_sent_popup_header') }}
            </div>
            <p class="need-help__time">
              {{ $t('qic_need_help_sent_popup_message') }}
            </p>
          </div>
          <BaseButton icon="none" @click="onClose">
            {{ $t('qic_ok') }}
          </BaseButton>
        </div>
      </div>
    </BasePopup>
  </Teleport>
</template>

<style lang="scss" scoped>
  @import './ContactUsWidget';
</style>
